import { TranslatePipe } from '@ngx-translate/core';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import 'moment/locale/fr';
import { flow, groupBy, map } from 'lodash-es';

import { Principal } from '../../../../shared/auth/principal.service';
import { FormatDatePipe } from '../../../_shared/format-date.pipe';
import {
    Session,
    SessionService,
    RegistrationDetailService,
    TrainingSessionCompany,
    GraphQlService,
} from '../../../../_services';

@Component({
    selector: 'session-details-registration',
    templateUrl: 'registration.component.html',
    styleUrls: [
        'registration.component.scss',
        '../../../_shared/label/label.component.scss',
    ],
    providers: [FormatDatePipe, TranslatePipe],
})
export class SessionDetailRegistrationComponent implements OnInit {
    @Input() sessionId;
    @Input() session: Session;
    @Output() deleted = new EventEmitter();
    @Output() priceUpdated = new EventEmitter();

    tsCompaniesLoading = true;
    tsCompanies: TrainingSessionCompany[] = [];

    isOneElementSelected: boolean = false;
    areAllSelected: boolean = false;
    users: any[] = [];
    usersNames: string[] = [];
    trainingSession: any = {};
    account: any;
    showCreateCompanyPanel: boolean = false;

    private isCurrentUserAdmin: boolean;

    constructor(
        private sessionService: SessionService,
        private principal: Principal,
        private registrationDetailService: RegistrationDetailService,
        private graphQlService: GraphQlService
    ) {}

    ngOnInit() {
        this.getSession();

        this.getRegistrations();

        this.getAllUsers();

        this.initAccount();

        this.registrationDetailService.isOneStudentSelected.subscribe(
            (item) => {
                this.isOneElementSelected = item;
            }
        );

        this.registrationDetailService.areAllSelected.subscribe((item) => {
            this.areAllSelected = item;
        });
    }

    fetchRegistrationsUsingGraphQl(trainingSessionId: number) {
        return this.graphQlService
            .post({
                query: `
                    query ($trainingSessionId: bigint) {
                        tsCompanies: plt_training_session_company(
                            where: {
                                registration: {
                                    trainingsession_id: { _eq: $trainingSessionId }
                                    deleted: { _eq: false }
                                }
                            }
                        ) {
                            id
                            price
                            priceCurrency: price_currency
                            billingMode: billing_mode
                            orderNumber: order_number
                            registration {
                                id
                                company {
                                    id
                                    name
                                }
                                financingInfo: financing_info {
                                    financingCompany: financing_company
                                }
                                registrationDetails: registration_details(
                                    where: { deleted: { _eq: false } }
                                ) {
                                    id
                                    contact {
                                        id
                                        firstName: first_name
                                        lastName: last_name
                                        email
                                        emailingMode: emailing_mode
                                    }
                                    student {
                                        id
                                        firstName: first_name
                                        lastName: last_name
                                        email
                                    }
                                    mailSent: mail_sent
                                    conventionSent: convention_sent
                                    price
                                }
                            }
                            salesPerson: sales_person {
                                id
                                firstName: first_name
                                lastName: last_name
                                email
                            }
                        }
                    }
                `,
                variables: {
                    trainingSessionId,
                },
                roles: ['platon/ROLE_ADMIN', 'world'],
            })
            .map(({ tsCompanies }) => {
                return tsCompanies.map((tsCompany) =>
                    this.mapGraphQlTsCompany(tsCompany)
                );
            });
    }

    mapGraphQlTsCompany(tsCompany) {
        // this mimics the structure of the data that is returned from the REST endpoint (/api/training-sessions/{id}/companies)
        const {
            billingMode,
            salesPerson,
            registration,
            id,
            price,
            priceCurrency,
            orderNumber,
        } = tsCompany;
        const { financingInfo, company } = registration;
        return {
            billingMode: billingMode,
            commercialName: salesPerson.firstName + ' ' + salesPerson.lastName,
            companyId: registration.company.id,
            contacts: flow(
                (registrationDetails) =>
                    groupBy(
                        registrationDetails,
                        (registrationDetail) => registrationDetail.contact.id
                    ),
                (registrationDetailsByContact) =>
                    map(registrationDetailsByContact, (registrationDetails) => {
                        const { id, firstName, lastName, email, emailingMode } =
                            registrationDetails[0].contact;
                        return {
                            id,
                            firstName,
                            lastName,
                            email,
                            emailingMode,
                            students: registrationDetails.map(
                                (registrationDetail) =>
                                    this.mapGraphQlRegistrationDetailToStudent(
                                        registrationDetail
                                    )
                            ),
                        };
                    })
            )(registration.registrationDetails),
            financed: Boolean(financingInfo && financingInfo.financingCompany),
            financingCompany: financingInfo && financingInfo.financingCompany,
            id: id,
            name: company.name,
            orderNumber,
            price,
            priceCurrency,
            registrationId: registration.id,
            salesPerson: {
                id: salesPerson.id,
                firstname: salesPerson.firstName,
                lastname: salesPerson.lastName,
                fullname: salesPerson.firstName + ' ' + salesPerson.lastName,
                email: salesPerson.email,
            },
        };
    }

    mapGraphQlRegistrationDetailToStudent(registrationDetail) {
        const {
            id: registrationDetailId,
            price,
            mailSent,
            conventionSent,
            student,
        } = registrationDetail;
        const { id, firstName, lastName, email, phone } = student;
        return {
            id,
            firstName,
            lastName,
            email,
            phone,
            registrationDetailId,
            price,
            mailSent,
            conventionSent,
        };
    }

    getRegistrations() {
        // this first request should way faster
        this.fetchRegistrationsUsingGraphQl(this.sessionId).subscribe(
            (tsCompanies) => {
                this.tsCompanies = tsCompanies;
                this.tsCompaniesLoading = false;
            }
        );
        // we also run the request through the back-end just to be sure all the old code gets run
        this.sessionService
            .getTrainingSessionRegistrations(this.sessionId)
            .subscribe((tsCompanies) => {
                // this overwrites whatever the graphql request returned, but should be the exact same
                this.tsCompanies = tsCompanies;
                this.tsCompaniesLoading = false;
            });
    }

    initAccount() {
        this.principal.identity().then((account) => {
            this.account = account;
            this.isCurrentUserAdmin =
                this.account.authorities.find((a) => a === 'ROLE_ADMIN') ===
                'ROLE_ADMIN';
        });
    }

    // récapitulatif des companies
    getRoomSizeWarning(): string {
        if (
            this.session.roomSize &&
            this.registrationDetailService.getCountRegistration(
                this.tsCompanies
            ) >= this.session.roomSize
        ) {
            return 'oversize';
        }
        return '';
    }

    getRoomCapacity(): string {
        if (this.session.roomSize) {
            return this.session.roomSize + ' places';
        }
        return '';
    }

    // doc generation

    getTotalRegistration(): string {
        let totalPrice = 0;

        this.tsCompanies.forEach((company) => {
            if (company.billingMode === 'FORFAIT') {
                totalPrice += company.price;
            } else {
                let t = 0;
                company.contacts.forEach((contact) => {
                    contact.students.forEach((student) => {
                        t++;
                    });
                });
                totalPrice += company.price * t;
            }
        });

        this.priceUpdated.emit({ totalPrice });
        return this.registrationDetailService.getPrice(
            totalPrice,
            this.session.priceCurrency
        );
    }

    getTotalCompanyRegistration(tsCompany: TrainingSessionCompany): number {
        return tsCompany.contacts.reduce(
            (tc, contact) =>
                tc +
                contact.students.reduce(
                    (ts, student) => (ts + student.price ? student.price : 0),
                    0
                ),
            0
        );
    }

    toggleRegistrations(): void {
        const contacts = [].concat.apply(
            [],
            this.tsCompanies.map((company) => company.contacts)
        );
        const students = [].concat.apply(
            [],
            contacts.map((contact) => contact.students)
        );
        students.forEach(
            (student) => (student.selected = !this.areAllSelected)
        );
        this.registrationDetailService.updateRegistrationSelection(
            this.tsCompanies
        );
    }

    getCountRegistration(): number {
        return this.registrationDetailService.getCountRegistration(
            this.tsCompanies
        );
    }

    // registration company
    addCompany() {
        this.showCreateCompanyPanel = true;
    }

    companyCreated(createdCompany): void {
        this.showCreateCompanyPanel = false;
        if (createdCompany.editingMode !== 'cancelEdition') {
            this.tsCompanies.push(createdCompany);
        }
    }

    private getSession(): void {
        this.sessionService
            .getTrainingSessionById(this.sessionId)
            .subscribe((trainingSession) => {
                this.trainingSession = trainingSession;
            });
    }

    private getAllUsers(): void {
        this.sessionService.getBackofficeUsers().subscribe((users) => {
            this.users = users;
            users.map((user) =>
                this.usersNames.push(user.firstname + ' ' + user.lastname)
            );
        });
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/session/detail/registration/registration.component.ts